import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { admin } from '@repletoryreach/sdk-api';
import { Card, Stack, Group, Text, Button, Title, SimpleGrid } from '@repletoryreach/sdk-ui';
import { NumberInput } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../../i18n/I18nProvider';

const KEY = ['admin', 'renewal-settings'];

type Field = keyof admin.RenewalSettings;

/** Each setting's range, as the service checks it. */
const FIELDS: Array<{ key: Field; min: number; max: number }> = [
  { key: 'grace_days', min: 1, max: 60 },
  { key: 'retry_hours', min: 1, max: 168 },
  { key: 'reminder_days', min: 1, max: 30 },
  { key: 'notice_days', min: 0, max: 14 }
];

/**
 * What happens when a plan's card is declined (owner, 11 October 2026: "Default 7 but make it a setting please as
 * well as the message reminders like frequency"). Plans are paid by card on the 1st; these four figures set the
 * grace before an unpaid plan ends, how often the card is tried again, how often the business is reminded, and how
 * early the "your card will be charged" notice goes.
 */
export function RenewalSettingsCard() {
  const t = useT();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => admin.getRenewalSettings() });
  const saved = q.data?.data;
  const [draft, setDraft] = useState<Partial<Record<Field, number | ''>>>({});
  // Seeded once, and again after a save: a refetch in the background must not wipe what is being typed.
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (!saved || seeded) return;
    setDraft({ ...saved });
    setSeeded(true);
  }, [saved, seeded]);

  const save = useMutation({
    mutationFn: (req: admin.RenewalSettings) => admin.saveRenewalSettings(req),
    onSuccess: (res) => {
      qc.setQueryData(KEY, res);
      setSeeded(false);
      notifySuccess(t('billing.renewal.saved'), t('billing.renewal.title'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('billing.renewal.title'))
  });

  const invalid = FIELDS.some(({ key, min, max }) => {
    const v = draft[key];
    return typeof v !== 'number' || v < min || v > max;
  });
  const dirty = Boolean(saved && FIELDS.some(({ key }) => draft[key] !== saved[key]));

  return (
    <Card withBorder data-testid="renewal-settings">
      <Stack gap="md">
        <Stack gap="xs">
          <Title order={5}>{t('billing.renewal.title')}</Title>
          <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('billing.renewal.body')}</Text>
        </Stack>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="sm">
          {FIELDS.map(({ key, min, max }) => (
            <NumberInput
              key={key}
              size="sm"
              label={t(`billing.renewal.${key}.label`)}
              description={t(`billing.renewal.${key}.hint`, { min, max })}
              value={draft[key] ?? ''}
              onChange={(v) => setDraft((d) => ({ ...d, [key]: typeof v === 'number' ? v : '' }))}
              min={min}
              max={max}
              step={1}
              disabled={!saved}
            />
          ))}
        </SimpleGrid>
        <Group justify="flex-end">
          <Button size="xs" loading={save.isPending} disabled={!dirty || invalid}
            onClick={() => save.mutate(draft as admin.RenewalSettings)}>
            {t('action.save')}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
